import React, {Fragment, FunctionComponent, useCallback, useEffect, useState} from "react";
import {ContactUs} from "@App/Components/ContactUs";
import {MapIcon, PriceComparisonIcon} from "@App/Components/common/Icons";
import {BottomSheet} from "@App/Components/BottomSheet";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit";
import {GarageList} from "@App/Components/GarageList";
import {MobileFilter} from "@App/Components/MobileFilter";
import {useParams} from "react-router-dom";
import {Garage} from "@App/types";
import {
    AvailableDateFilter,
    BrandFilter,
    DistanceFilter,
    GarageNameFilter,
    LocationFilter,
    PremiumServiceFilter
} from "@App/Components/Filters";
import {SortListBox} from "@App/Components/SortListBox";
import 'react-calendar/dist/Calendar.css';
import {useScrollToTop} from "@App/hooks/useScrollToTop";
import {ServiceFAQs} from "@App/Components/ServiceFAQs";
import {useTranslation} from "react-i18next";
import {Filters} from "@App/types/enums";
import {Loader} from "@App/Components/Loader";
import {useServiceFunnel} from "@App/hooks/useServiceFunnel";
import {ContactPartner} from "@App/Components/ContactPartner";
import {ManufacturerFilter} from "@App/Components/Filters/ManufacturerFilter";
import {
    OrganizationIsArvalAndServiceIsWheelChange,
    OrganizationIsNotAkfAndNotAkfLangzeitmiete,
    OrganizationIsNotArvalOrServiceIsNotWheelChange,
    ServiceIsWheelChangeAndOrganizationIsNio,
    ServiceIsWheelChangeTiresstoredAndOrganizationIsNio,
    ServiceIsWheelReplacementAndOrganizationIsNio
} from "@App/utils/conditionals/combinations";
import {
    OrganizationIsAthlon,
    OrganizationIsDBConnect,
    OrganizationIsLeaseplan,
    OrganizationIsMobilityConcept,
    OrganizationIsRepareo
} from "@App/utils/conditionals/organization";
import {ServiceHasCustomSearchHeadline, ServiceIsInspection} from "@App/utils/conditionals/services";
import {FilterOverview} from "@App/Components/Filters/FilterOverview";
import {FeedbackHint} from "@App/Components/Partials/FeedbackHint";
import {PremiumServiceFilterNoRequiredInput} from "@App/Components/Filters/PremiumServiceFilterNoRequiredInput";
import {PremiumServiceFilterSingularInput} from "@App/Components/Filters/PremiumServiceFilterSingularInput";
import BackButton from "../../Components/BackButton";


export type GaragesProps = {
    embedded?: boolean
    onSubmit?: (garage: Garage) => void
}

export const Garages: FunctionComponent<GaragesProps> = ({
                                                             embedded = false,
                                                             onSubmit
                                                         }: GaragesProps) => {
    const {t} = useTranslation()
    useScrollToTop()

    const [currentlyShownFilter, setCurrentlyShownFilter] = useState(Filters.None)
    const [toggleMap, setToggleMap] = useState(false)
    const [hasPremiumServices, setHasPremiumServices] = useState(false)

    const dispatch = useDispatch();
    const {chooseGarage, changeSortByFilter, chooseSlot} = bindActionCreators(actionCreators, dispatch)

    const organization = useSelector((state: State) => state.orders.organization)
    const previousGarage = useSelector((state: State) => state.orders.garage)
    const service = useSelector((state: State) => state.orders.service)
    const brand = useSelector((state: State) => state.orders.brand)
    const place = useSelector((state: State) => state.orders.place)
    const sortBy = useSelector((state: State) => state.filters.sortBy)
    const selectedPremiumServices = useSelector((state: State) => state.orders.premiumServices)
    const bevSupport = useSelector((state: State) => state.filters.bevSupport)
    const commercialVehicleSupport = useSelector((state: State) => state.filters.commercialVehicleSupport)


    const {serviceSlug} = useParams()
    useServiceFunnel(serviceSlug)

    useEffect(() => {
        if (service && place) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'view list',
                'service': service,
                'location': place,
                'organization': organization.id
            });
        }

    }, [service, place])

    // remove previously chosen slots
    useEffect(() => {
        // chooseGarage(null)
        chooseSlot(null)
    }, [])

    useEffect(() => {
        // change standard sorting to recommended if service is inspection
        if (!!service && OrganizationIsRepareo(organization) && ['inspektion', 'bremsenwechsel'].includes(service.slug)) {
            changeSortByFilter('recommended')
        }
    }, [service])

    const selectGarage = (garage: Garage) => {
        chooseGarage(garage)
        onSubmit && onSubmit(garage)
    }

    const changeFilter = (type: Filters) => {
        setCurrentlyShownFilter(type)
    }

    const closeFilters = () => {
        setCurrentlyShownFilter(Filters.None)
    }

    const onChangeGarage = useCallback((garage) => {
        selectGarage(garage)
    }, [])

    const onChangeSorting = useCallback((sortBy) => {
        changeSortByFilter(sortBy)
    }, [])

    return (
        <Fragment>
            <div className="bg-neutral-100 py-5">
                <div className="container">
                    <div className="mb-4 md:hidden">
                        {service && [132, 94].includes(+service.id) && (
                            <div className="text-lg font-bold font-serif mb-3">
                                {t("Pruefstellen in Ihrer Naehe", {service: service.pivot.display_name ?? service.name})}
                            </div>
                        )}
                        {service && ![132, 94].includes(+service.id) && (
                            <div className="text-lg font-bold font-serif mb-3">
                                {t("Werkstaetten in Ihrer Naehe", {service: service.pivot.display_name ?? service.name})}
                            </div>
                        )}
                        <MobileFilter
                            filterPlaceOpen={currentlyShownFilter === Filters.Place}
                            filterServiceOpen={currentlyShownFilter === Filters.Service}
                            filterDateOpen={currentlyShownFilter === Filters.Date}
                            filterBrandOpen={currentlyShownFilter === Filters.Brand}
                            onChangeMap={setToggleMap}
                            onChange={changeFilter}
                            hasPremiumServices={hasPremiumServices}
                        />
                        <BackButton/>
                    </div>
                    <div className="hidden md:flex md:justify-between md:items-center">
                        <div className="flex items-center">
                            <div className="mr-4">
                                <PriceComparisonIcon/>
                            </div>
                            <div>
                                <div className="text-2xl font-serif">
                                    {service && ServiceHasCustomSearchHeadline(service) && (
                                        <>{service.pivot.display_search_headline.replace('%ORGANIZATION%', organization.name)}</>
                                    )}
                                    {service && [132, 94].includes(+service.id) && !ServiceHasCustomSearchHeadline(service) && (
                                        <>{t("Partnerpruefstellen in Ihrer Naehe", {organization: organization.name})}</>
                                    )}
                                    {service && ![132, 94].includes(+service.id) && !ServiceHasCustomSearchHeadline(service) && (
                                        <>{t("Partnerwerkstaetten in Ihrer Naehe", {organization: organization.name})}</>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center">
                            <div className={`flex items-center ${toggleMap ? "hidden" : ""}`}>
                                <div className="font-bold text-sm mr-4">{t('sort by')}</div>
                                <div className="w-52 mr-4">
                                    <SortListBox selected={sortBy} onChange={onChangeSorting} service={service}/>
                                </div>
                            </div>
                            <div onClick={() => setToggleMap(!toggleMap)} className="cursor-pointer mt-1">
                                <div
                                    className="flex items-center rounded shadow bg-white py-2 px-3">
                                    <div className="mr-2">
                                        <MapIcon/>
                                    </div>
                                    <div>{toggleMap ? t('Listenansicht') : t('Kartenansicht')}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="md:flex md:pt-10">
                        <div className="hidden md:block">
                        <BackButton/>
                            {(!service || !place) && (
                                <div className="h-screen w-screen grid place-items-center">
                                    <Loader/>
                                </div>
                            )}
                            {service && place && (
                                <div
                                    className="flex flex-col justify-center mr-12 max-w-[270px] text-neutral-700">
                                    {(!OrganizationIsRepareo(organization) && !OrganizationIsLeaseplan(organization)) &&(
                                        <div className="mb-8">
                                            <FilterOverview organization={organization} service={service} brand={brand} bevSupport={bevSupport} commercialVehicleSupport={commercialVehicleSupport}/>
                                        </div>
                                    )}
                                    {organization.settings.garage_list.filters.includes('search') && (
                                        <div className="mb-8">
                                            <div className="relative">
                                                <GarageNameFilter/>
                                            </div>
                                        </div>
                                    )}
                                    {organization.settings.garage_list.filters.includes('brand') && !brand && (
                                        <div className="mb-8">
                                            <div className="font-bold text-sm mb-3">{t('Marke')}</div>
                                            <div>
                                                <BrandFilter/>
                                            </div>
                                        </div>
                                    )}
                                    {organization.settings.garage_list.filters.includes('place') && (
                                        <Fragment>
                                            <div className="mb-8">
                                                <div className="font-bold text-sm mb-3">{t('Umgebung')}</div>
                                                <div>
                                                    <LocationFilter shouldWriteToUrl={true}/>
                                                </div>
                                            </div>
                                            <div className="mb-8">
                                                <DistanceFilter/>
                                            </div>
                                        </Fragment>
                                    )}
                                    {ServiceIsInspection(service) && OrganizationIsMobilityConcept(organization) && (
                                        <div className="mb-8">
                                            <ManufacturerFilter/>
                                        </div>
                                    )}

                                    {organization.settings.garage_list.filters.includes('date') && (
                                        <div className="mb-8">
                                            <div className="font-bold text-sm mb-3">{t('Verfuegbarkeit')}</div>
                                            <AvailableDateFilter/>
                                        </div>
                                    )}
                                    {(!ServiceIsWheelChangeAndOrganizationIsNio(organization, service) && !ServiceIsWheelReplacementAndOrganizationIsNio(organization, service) && !ServiceIsWheelChangeTiresstoredAndOrganizationIsNio(organization, service) && !OrganizationIsAthlon(organization)) && (
                                        <div className="mb-8">
                                            <PremiumServiceFilter
                                                organization={organization}
                                                service={service}
                                                premiumServices={selectedPremiumServices}
                                                onLoaded={(hasItems) => setHasPremiumServices(hasItems)}
                                            />
                                        </div>
                                    )}
                                    {(ServiceIsWheelChangeAndOrganizationIsNio(organization, service) || ServiceIsWheelChangeTiresstoredAndOrganizationIsNio(organization, service)) && (
                                        <div className="mb-8">
                                            <PremiumServiceFilterNoRequiredInput
                                                organization={organization}
                                                service={service}
                                                premiumServices={selectedPremiumServices}
                                                onLoaded={(hasItems) => setHasPremiumServices(hasItems)}
                                            />
                                        </div>
                                    )}
                                    {(ServiceIsWheelReplacementAndOrganizationIsNio(organization, service) || OrganizationIsAthlon(organization)) && (
                                        <div className="mb-8">
                                            <PremiumServiceFilterSingularInput
                                                organization={organization}
                                                service={service}
                                                premiumServices={selectedPremiumServices}
                                                onLoaded={(hasItems) => setHasPremiumServices(hasItems)}
                                            />
                                        </div>
                                    )}


                                    {service && (
                                        <ServiceFAQs sidebar/>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className={`flex flex-col items-center w-full`}>
                            {service && place && (
                                <GarageList
                                    service={service}
                                    onChange={onChangeGarage}
                                    toggleMap={toggleMap}
                                    onToggleMap={setToggleMap}
                                    mobileServiceFAQs
                                />
                            )}
                        </div>
                    </div>
                    {(!embedded && OrganizationIsNotAkfAndNotAkfLangzeitmiete(organization)) && (
                        <div className="md:grid md:grid-cols-[50%_50%] md:pt-20 md:mb-20">
                            <div className="pt-12 md:pt-0 md:min-w-1/2 md:pr-16">
                                {OrganizationIsDBConnect(organization) && (
                                    <ContactPartner/>
                                )}
                                {!OrganizationIsDBConnect(organization) && (
                                    <FeedbackHint/>
                                )}
                            </div>

                            <div className="pt-5 md:pt-0 md:pl-16">
                                <ContactUs titleHidden={OrganizationIsDBConnect(organization)}/>
                            </div>
                            {OrganizationIsDBConnect(organization) && (
                                <div className="pt-5 md:pt-0">
                                    <FeedbackHint/>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {place && (
                <BottomSheet title={t('Umgebung')} open={currentlyShownFilter === Filters.Place}
                             onConfirm={() => closeFilters()}
                             onClose={() => closeFilters()}>
                    <p>
                        {t('proximity-search-text')}
                    </p>
                    <LocationFilter className={'mt-8 mb-6'}/>
                    <DistanceFilter/>
                </BottomSheet>
            )}
            <BottomSheet title={t('Datum')} open={currentlyShownFilter === Filters.Date}
                         onConfirm={() => closeFilters()}
                         onClose={() => closeFilters()}>
                <AvailableDateFilter show={true}/>
            </BottomSheet>
            {hasPremiumServices && OrganizationIsNotArvalOrServiceIsNotWheelChange(organization, service) && (
                <BottomSheet title={t('Zusatzservices')} open={currentlyShownFilter === Filters.Service}
                             onConfirm={() => closeFilters()}
                             onClose={() => closeFilters()}>
                    <PremiumServiceFilter organization={organization} service={service} premiumServices={selectedPremiumServices}/>
                </BottomSheet>
            )}
            {service && OrganizationIsArvalAndServiceIsWheelChange(organization, service) && (
                <BottomSheet title={'Filter'} open={currentlyShownFilter === Filters.Service}
                             onConfirm={() => closeFilters()}
                             onClose={() => closeFilters()}>
                    {hasPremiumServices && (<div className="font-bold text-sm mb-3 mt-4">{t('Zusatzservices')}</div>)}
                    <PremiumServiceFilter organization={organization} service={service} premiumServices={selectedPremiumServices}/>
                </BottomSheet>
                )}
            <BottomSheet title={t('Werkstattname suchen')} open={currentlyShownFilter === Filters.Search}
                         onConfirm={() => closeFilters()} onClose={() => closeFilters()}>
                <GarageNameFilter/>
            </BottomSheet>
            <BottomSheet title={"Marke suchen"} open={currentlyShownFilter === Filters.Brand}
                         onConfirm={() => closeFilters()} onClose={() => closeFilters()}>
                <div className="mb-24">
                    <BrandFilter/>
                </div>
            </BottomSheet>
        </Fragment>
    );
}
